/**
 * Auth-aware fetch client for the Garage OS API (web).
 * Mirrors the mobile src/lib/api.ts contract exactly.
 */
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL ?? "";
export const API_BASE = `${BASE}/api`;
export const API_ORIGIN = BASE;
export const TOKEN_KEY = "garage_access_token";

export async function api(path, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY) || "";
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}/api${path}`, { ...options, headers });
  const text = await res.text();
  let body = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = { raw: text };
  }
  if (!res.ok) {
    const message =
      typeof body?.detail === "string"
        ? body.detail
        : body?.detail?.message ||
          body?.detail?.error ||
          body?.error ||
          `HTTP ${res.status}`;
    // Read-only (cancelled subscription): surface a clear toast app-wide so a
    // blocked write action always gets feedback, not just a silent failure.
    if (res.status === 403 && body?.detail?.error === "subscription_readonly") {
      toast.error(body.detail.message || "Your account is read-only.");
    }
    const err = new Error(message);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

/** Create a supplier, warning (via confirm) on a matching name. Returns the
 *  created supplier, or null if the user cancelled the duplicate warning. */
export async function createSupplierWithDupCheck(name, extra = {}) {
  try {
    return await api("/suppliers", { method: "POST", body: JSON.stringify({ name, ...extra }) });
  } catch (e) {
    if (e.status === 409 && e.body?.detail?.error === "duplicate_supplier") {
      // eslint-disable-next-line no-alert
      if (!window.confirm(e.body.detail.message)) return null;
      return await api("/suppliers", { method: "POST", body: JSON.stringify({ name, allow_duplicate: true, ...extra }) });
    }
    throw e;
  }
}

/** Upload an image (receipts, check-in/out photos) via multipart. Returns {id, url, token}. */
export async function uploadImage(file, { context = "receipt", jobId } = {}) {
  const token = localStorage.getItem(TOKEN_KEY) || "";
  const fd = new FormData();
  fd.append("file", file);
  const qs = new URLSearchParams({ context, ...(jobId ? { job_id: jobId } : {}) });
  const res = await fetch(`${BASE}/api/media/upload-doc?${qs}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.detail || `Upload failed (${res.status})`);
  return { ...body, url: `${BASE}${body.url}` };
}


export const money = (n) =>
  typeof n === "number"
    ? `£${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
    : "£0.00";

export const formatVRM = (v) =>
  (v || "").toUpperCase().replace(/\s+/g, "").replace(/^(.{4})/, "$1 ").trim();

/** Single source of truth for VAT in the UI: effective rate (%) for a garage.
 * Returns 0 when the garage is not VAT-registered (VAT OFF) so VAT is hidden everywhere. */
export const garageVatRate = (g) => {
  const reg = g?.vat_registered ?? !!g?.vat_number;
  if (!reg) return 0;
  const rate = g?.default_vat_rate ?? g?.vat_rate;
  return rate != null ? Number(rate) : 20;
};


/** Cross-platform PDF download: fetch with auth token, force browser download. */
export async function downloadPdf(path, filename) {
  const token = localStorage.getItem(TOKEN_KEY) || "";
  const res = await fetch(`${BASE}/api${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`Download failed (${res.status}): ${t.slice(0, 120)}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Fetch a PDF and return an object URL for embedding (caller must revoke it).
export async function fetchPdfBlobUrl(path) {
  const token = localStorage.getItem(TOKEN_KEY) || "";
  const res = await fetch(`${BASE}/api${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`PDF generation failed (${res.status}): ${t.slice(0, 160)}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

